const text = (value) => String(value ?? "").trim();

// Search normalization: accents, punctuation and case never block a match.
export function normalizeAliasIdentity(value) {
  return text(value).replace(/[’‘]/g, "'").replace(/[–—]/g, "-").normalize("NFKD")
    .replace(/[^\x00-\x7F]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

// UI-only owner for the unsaved company. Never include it in an API payload.
export const NEW_BILLING_ACCOUNT_ID = "new-billing-account";

export function buildNewBillingAccountAssociations(data, account = {}) {
  const snapshot = buildAliasesSnapshot(data);
  snapshot.accounts = [...snapshot.accounts, { id: NEW_BILLING_ACCOUNT_ID, name: account.name || "Nouvelle entreprise" }];
  const owners = { ...snapshot.owners };
  for (const id of account.client_ids || account.clients?.map((row) => row.id) || []) {
    const key = `client:${id}`;
    if (!(key in owners) || owners[key]) {
      throw new Error("Le client présélectionné n’est plus disponible. Rechargez le répertoire puis rouvrez la fiche.");
    }
    owners[key] = NEW_BILLING_ACCOUNT_ID;
  }
  return { snapshot, owners };
}

export function buildNewBillingAccountAssociationsPayload({ snapshot, owners }) {
  return { client_ids: snapshot.entries.filter((row) => owners[row.key] === NEW_BILLING_ACCOUNT_ID).map((row) => row.id) };
}

export function buildAliasesSnapshot({ accounts = [], sourceClients = [] } = {}) {
  const activeAccounts = accounts.filter((account) => account.is_active !== false)
    .toSorted((a, b) => text(a.name).localeCompare(text(b.name), "fr", { sensitivity: "base" }));
  const entries = new Map();
  const addClient = (row, owner) => {
    const key = `client:${row.id}`;
    entries.set(key, { ...row, key, kind: "client", label: row.client_name || row.correspondence_name || "Client sans nom",
      detail: [row.client_code || "Sans code", row.address].filter(Boolean).join(" · "), owner: owner || null });
  };
  activeAccounts.forEach((account) => (account.clients || []).forEach((row) => addClient(row, account.id)));
  sourceClients.forEach((row) => addClient(row, row.billing_account_id || row.billing_account?.id));
  const sortedEntries = [...entries.values()].toSorted((a, b) => `${a.label} ${a.detail}`.localeCompare(`${b.label} ${b.detail}`, "fr"));
  return { accounts: activeAccounts, entries: sortedEntries, owners: Object.fromEntries(sortedEntries.map((row) => [row.key, row.owner])) };
}

export function aliasChanges(snapshot, owners) {
  return snapshot.entries.filter((entry) => (owners[entry.key] || null) !== entry.owner);
}

export function buildAliasesPayload(snapshot, owners) {
  const changed = aliasChanges(snapshot, owners);
  const affected = new Set(changed.flatMap((entry) => [entry.owner, owners[entry.key]]).filter(Boolean));
  return {
    accounts: snapshot.accounts.filter((account) => affected.has(account.id)).map((account) => ({ account_id: account.id, expected_version: account.version })),
    clients: changed.map((row) => ({ client_id: row.id, expected_billing_account_id: row.owner, billing_account_id: owners[row.key] || null })),
  };
}

export function filterAliasAccounts(snapshot, owners, query, { includeUnlinked = false, accountId = null } = {}) {
  if (accountId) return snapshot.accounts.filter((account) => account.id === accountId);
  const needle = normalizeAliasIdentity(query);
  // Keep original owners visible until removals are saved or discarded.
  const visibleIds = new Set(snapshot.entries.flatMap((row) => [owners[row.key], row.owner]).filter(Boolean));
  return snapshot.accounts.filter((account) => (includeUnlinked || visibleIds.has(account.id)) && (!needle || normalizeAliasIdentity([
    account.name, account.billing_address, account.billing_email, ...snapshot.entries.filter((row) => owners[row.key] === account.id || row.owner === account.id)
      .flatMap((row) => [row.label, row.detail]),
  ].join(" ")).includes(needle)));
}
